export enum ControllerStates {
	Pristine,
	Open,
	Closed,
	Error,
}

interface StatePristine<_T> {
	state: ControllerStates.Pristine;
	data?: never;
}

interface StateOpen<T> {
	state: ControllerStates.Open;
	data: T;
}

interface StateClosed<C> {
	state: ControllerStates.Closed;
	data: C;
}

interface StateError<K> {
	state: ControllerStates.Error;
	data: K;
}

type State<T, C, K> = StatePristine<unknown> | StateOpen<T> | StateClosed<C> | StateError<K>;

type OnEventFnWithPayload<T> = (data: T) => void;

/**
 * ## @visiba/axon
 * Abstraction to implement and override generic event functions. */
export abstract class AbstractSocketEvents<T = unknown, K = T, C = T> {
	protected state: State<T, C, K> = { state: ControllerStates.Pristine };
	protected storedUncalledOnOpenEvents: OnEventFnWithPayload<T>[] = [];
	protected storedUncalledOnCloseEvents: OnEventFnWithPayload<C>[] = [];
	protected storedUncalledOnErrorEvents: OnEventFnWithPayload<K>[] = [];
	protected storedUncalledOnReconnect: OnEventFnWithPayload<null>[] = [];
	protected storedUncalledOnReconnected: OnEventFnWithPayload<null>[] = [];

	/**
	 * Fires when instance has opened.
	 */
	public set onOpen(fn: OnEventFnWithPayload<T>) {
		if (this.state.state === ControllerStates.Open) {
			fn(this.state.data);

			return;
		}

		this.storedUncalledOnOpenEvents.push(fn);
	}

	/**
	 * Fires when instance is closed.
	 */
	public set onClose(fn: OnEventFnWithPayload<C>) {
		if (this.state.state === ControllerStates.Closed) {
			fn(this.state.data);

			return;
		}

		this.storedUncalledOnCloseEvents.push(fn);
	}

	/**
	 * Fires when the socket is being set in a reconnection state.
	 */
	public set onReconnect(fn: OnEventFnWithPayload<null>) {
		this.storedUncalledOnReconnect.push(fn);
	}

	/**
	 * Fires when the socket is being set in a reconnected state.
	 */
	public set onReconnected(fn: OnEventFnWithPayload<null>) {
		this.storedUncalledOnReconnected.push(fn);
	}

	/**
	 * Fires when instance has an error.
	 *
	 * @deprecated
	 */
	public set onError(fn: OnEventFnWithPayload<K>) {
		if (this.state.state === ControllerStates.Error) {
			fn(this.state.data);

			return;
		}

		this.storedUncalledOnErrorEvents.push(fn);
	}
}
